// The function charged to locate the API adapter object presented by the LMS.
// As described in section 3.3.6.1 of the documentation.

// TODO: Test with moodle and other platforms
const findAPI = function(win) {
  let tries = 0;

  while (win && tries < 8) {
    if (win.API) {
      console.log('Find ScormAPI in %d tries', tries);
      return win.API;
    }

    if (win.opener) {
      win = win.opener;
    } else if (win.parent && win.parent !== win) {
      win = win.parent;
    } else {
      win = null;
    }

    tries++;
  }

  throw new Error(
    `A valid SCORM API Adapter can not be found in the window or in the window.opener after ${tries} recursive attempts`
  );
};

const scormStatusCodeString = {
  '0': 'NoError',
  '101': 'GeneralException',
  '102': 'ServerBusy',
  '201': 'InvalidArgumentError',
  '202': 'ElementCannotHaveChildren',
  '203': 'ElementIsNotAnArray',
  '301': 'NotInitialized',
  '401': 'NotImplementedError',
  '402': 'InvalidSetValue',
  '403': 'ElementIsReadOnly',
  '404': 'ElementIsWriteOnly',
  '405': 'IncorrectDataType',
};

class ScormError extends Error {
  constructor(code, message, logs = []) {
    super(`SCORM OPERATION ERROR > ${message}`);
    this.code = code;
    this.logs = logs;
  }
}

export class ScormApiWrapper {
  constructor() {
    this.API = null;
    this.sessionLogs = [];
    this.sessionIsFinished = false;
  }

  configure() {
    // It can throw an exception
    this.API = findAPI(window);
  }

  initialize() {
    this.configure();

    // This is necessary for e-greta.ac-versailles.fr
    // which has a custom scorm API
    // It doesn't support Initialize after Finish
    if (typeof this.API._InitFin === 'function') {
      this.API._InitFin();
    }

    const result = this.API.LMSInitialize('');
    this._assertOperation(result);
  }

  isInitialized() {
    return !!this.API && !this.sessionIsFinished;
  }

  getScormValue(parameter) {
    const value = this.API.LMSGetValue(parameter);
    this._logOperation('LMSGetValue', {parameter: parameter, value: value});
    return value;
  }

  setScormValue(parameter, value) {
    const result = this.API.LMSSetValue(parameter, value);
    this._logOperation('LMSSetValue', {parameter: parameter, value: value});
    this._assertOperation(result);
  }

  // A convenience method with a more
  commit() {
    if (this.sessionIsFinished) {
      console.warn('SCORM | Commit action called after a successful finish');
      return;
    }
    const result = this.API.LMSCommit('');
    this._logOperation('LMSCommit');
    this._assertOperation(result);
  }

  // A convenience method that do the correct sequence of calls to close the communication with the lms
  finish() {
    if (this.sessionIsFinished) {
      console.warn('SCORM | Finish action called after a successful finish');
      return;
    }
    this.commit();

    const result = this.API.LMSFinish('');
    this._logOperation('LMSFinish');
    this._assertOperation(result);
    this.sessionIsFinished = true;
  }

  getLastError() {
    const error = this.API.LMSGetLastError();

    return {
      code: parseInt(error, 10),
      message: scormStatusCodeString[error],
    };
  }

  _assertOperation(result) {
    if (String(result) !== 'true') {
      const {code, message} = this.getLastError();

      throw new ScormError(code, message, this.sessionLogs);
    }
  }

  _logOperation(scormAPIFn, scormAPIFnArguments) {
    const scormLastErrCode = this.API.LMSGetLastError();
    const log = {
      timestamp: Date.now(),
      scormFn: scormAPIFn,
      scormFnArgs: scormAPIFnArguments,
      errorCode: scormLastErrCode,
      errorCodeString: scormStatusCodeString[scormLastErrCode],
      errorCodeStringLMS: this.API.LMSGetErrorString(scormLastErrCode),
      diagnostic: this.API.LMSGetDiagnostic(''),
    };

    this.sessionLogs.push(log);
  }
}

export default new ScormApiWrapper();
